import * as amplitude from '@amplitude/analytics-browser';
import { ENV } from '@user-portal/helpers';

// helpers
import Utils from '../helpers/Utils';

// classes
import AbstractSystem from './AbstractSystem';

// constants
import { SYSTEMS_NAMES } from '../enums';

// providers
import DataProvider from '../DataProvider';

// types
import { Config } from '../Interfaces/Config';
import {
  EventDataTrack,
  PurchaseData,
  IPrepareTrackParams,
  IPurchaseNew,
  ITrackNewAnalytics,
} from '../Interfaces/Events';

export default class AmplitudeAnalytic extends AbstractSystem {
  private envMode: ENV;
  private releaseDate: unknown;

  constructor() {
    super(SYSTEMS_NAMES.amplitude, false);

    this.envMode = ENV.develop;
    this.releaseDate = 'default_date';
  }

  public init({ id, config }: { id: string; config?: Config }) {
    if (!this.getIsInit() && config) {
      if (!config) {
        DataProvider.handleError(new Error('Config is required'));

        return;
      }

      if (!config.releaseDate) {
        DataProvider.handleError(new Error('Release date is not defined'));

        return;
      }

      if (!config.envMode) {
        DataProvider.handleError(new Error('Env mode is not defined'));

        return;
      }

      Utils.setReleaseDate(config.releaseDate);

      this.envMode = config.envMode;
      this.releaseDate = config.releaseDate;

      amplitude.init(id);
      this.isInit = true;
    }
  }

  public async track(eventName: string, eventData: EventDataTrack | PurchaseData = {}): Promise<void> {
    try {
      const trackParams = await this.prepareTrackParams({
        eventName,
        eventData,
      });
      amplitude.track(eventName, trackParams);

      console.log(`Event ${eventName} successfully sent.`);
    } catch (err) {
      DataProvider.handleError(err);

      console.error(`Error while sending ${eventName}`);

      // @ts-ignore
      console.error(err, err.stack);
    }
  }

  public async trackNew({ eventName, eventAction, eventData = {} }: ITrackNewAnalytics): Promise<void> {
    const trackParams = await this.prepareTrackParams({
      eventName,
      eventAction,
      eventData,
    });

    try {
      amplitude.track(eventName, trackParams);

      console.log(`Event ${eventName} successfully sent.`);
    } catch (err) {
      DataProvider.handleError(err);

      console.error(`Error while sending ${eventName}`);
      // @ts-ignore
      console.error(err, err.stack);
    }
  }

  public async purchase(event_name: string, data: PurchaseData) {
    await this.track(event_name || 'purchase', data);
  }

  public async purchaseNew({ eventName, data }: IPurchaseNew) {
    const eventNamePurchase = eventName || 'purchase';

    await this.trackNew({ eventName: eventNamePurchase, eventData: data });
  }

  public async setUserProperties(data: Record<string, amplitude.Types.ValidPropertyType | null | undefined>) {
    const identifyEvent = new amplitude.Identify();

    for (const key in data) {
      // @TODO added default empty string for `value`
      const value = data[key] || '';
      if (value !== null) {
        identifyEvent.set(key, value);
      } else {
        identifyEvent.unset(key);
      }
    }

    amplitude.identify(identifyEvent);

    const typeOfUserId = typeof data.user_id as unknown;

    if (typeOfUserId === 'string' || typeOfUserId === 'number') {
      amplitude.setUserId(String(data.user_id));
    }
  }

  public async clearUserProperties() {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.clearAll();
    amplitude.identify(identifyEvent);
    amplitude.setUserId(undefined);
  }

  private async prepareTrackParams({ eventName = 'purchase', eventAction, eventData }: IPrepareTrackParams) {
    const recordData = await Utils.getAnalyticData(eventName, eventAction);

    const finalRecordData = {
      ...recordData,
      ...eventData,
      mode: this.envMode,
      release_date: this.releaseDate,
    };

    console.log('BEFORE SEND TO AMPLITUDE DATA', finalRecordData);

    return finalRecordData;
  }
}
